/**
 * Utilidades para mapear campos comunes a iconos Heroicons
 * Facilita la asignación consistente de iconos a campos de formulario
 */

import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  IdentificationIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  BanknotesIcon,
  CreditCardIcon,
  CalendarIcon,
  DocumentTextIcon,
  TruckIcon,
  TagIcon,
  ShoppingBagIcon,
  MagnifyingGlassIcon,
  HashtagIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'

/**
 * Mapeo de tipos de campos comunes a sus iconos correspondientes
 */
export const fieldIcons = {
  // Información personal
  firstName: UserIcon,
  lastName: UserIcon,
  name: UserIcon,
  email: EnvelopeIcon,
  phone: PhoneIcon,
  telefono: PhoneIcon,
  document: IdentificationIcon,
  documento: IdentificationIcon,
  tipoDocumento: IdentificationIcon,
  tipo_documento: IdentificationIcon,
  
  // Direcciones
  address: MapPinIcon,
  address_1: MapPinIcon,
  address_2: MapPinIcon,
  direccion: MapPinIcon,
  city: BuildingOfficeIcon,
  ciudad: BuildingOfficeIcon,
  state: BuildingOfficeIcon,
  departamento: BuildingOfficeIcon,
  postcode: HashtagIcon,
  codigoPostal: HashtagIcon,
  
  // Pagos
  payment_method: CreditCardIcon,
  metodoPago: CreditCardIcon,
  forma_pago: BanknotesIcon,
  formaPago: BanknotesIcon,
  transaction_id: HashtagIcon,
  transactionId: HashtagIcon,
  monto: CurrencyDollarIcon,
  valor: CurrencyDollarIcon,
  precio: CurrencyDollarIcon,
  costo: CurrencyDollarIcon,
  
  // Productos y pedidos
  producto: ShoppingBagIcon,
  product: ShoppingBagIcon,
  sku: TagIcon,
  cantidad: HashtagIcon,
  quantity: HashtagIcon,
  descuento: CurrencyDollarIcon,
  discount: CurrencyDollarIcon,
  
  // Envío
  envio: TruckIcon,
  shipping: TruckIcon,
  transportadora: TruckIcon,
  guia: DocumentTextIcon,
  tracking: HashtagIcon,
  
  // Fechas y tiempo
  fecha: CalendarIcon,
  date: CalendarIcon,
  fechaCreacion: CalendarIcon,
  fechaModificacion: CalendarIcon,
  hora: ClockIcon,
  time: ClockIcon,
  
  // Búsqueda
  search: MagnifyingGlassIcon,
  buscar: MagnifyingGlassIcon,
  
  // Notas y descripción
  notes: DocumentTextIcon,
  notas: DocumentTextIcon,
  descripcion: DocumentTextIcon,
  description: DocumentTextIcon,
  customer_note: DocumentTextIcon,
  
  // Estado y validación
  status: CheckCircleIcon,
  estado: CheckCircleIcon,
  tipoLead: TagIcon,
  tipo_lead: TagIcon,
  tipoCliente: UserIcon,
  tipo_cliente: UserIcon,
} as const

/**
 * Obtiene el icono correspondiente para un campo dado
 * @param fieldName - Nombre del campo (puede ser en camelCase o snake_case)
 * @returns Componente de icono Heroicon o undefined si no se encuentra
 */
export function getFieldIcon(fieldName: string) {
  // Normalizar el nombre del campo
  const normalized = fieldName.toLowerCase().trim()
  
  // Buscar coincidencia exacta
  if (fieldIcons[normalized as keyof typeof fieldIcons]) {
    return fieldIcons[normalized as keyof typeof fieldIcons]
  }
  
  // Buscar coincidencia parcial (para campos como "billing_address", "shipping_address")
  for (const [key, icon] of Object.entries(fieldIcons)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return icon
    }
  }
  
  return undefined
}

/**
 * Mapeo de tipos de input a iconos sugeridos
 */
export const inputTypeIcons = {
  email: EnvelopeIcon,
  tel: PhoneIcon,
  phone: PhoneIcon,
  text: undefined, // Sin icono por defecto para texto genérico
  number: HashtagIcon,
  date: CalendarIcon,
  time: ClockIcon,
  password: undefined, // Se maneja con iconos especiales (ojo)
  search: MagnifyingGlassIcon,
} as const

/**
 * Obtiene un icono sugerido basado en el tipo de input
 * @param inputType - Tipo de input HTML
 * @returns Componente de icono Heroicon o undefined
 */
export function getInputTypeIcon(inputType: string) {
  return inputTypeIcons[inputType.toLowerCase() as keyof typeof inputTypeIcons]
}

